import { notyf } from '@modules';

class ContactForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.uploadButton = this.querySelector('[data-upload-button]');
    this.fileUpload = this.querySelector('file-upload');
    this.submitButton = this.querySelector('[type="submit"]');
    this.originalSubmitText = this.submitButton?.textContent || 'Submit';
    this.isSubmitting = false;
    this.uploadedFiles = null;

    this.bytescaleAccountId = this.dataset.bytescaleAccountId || window.BYTESCALE_ACCOUNT_ID;
    this.bytescaleApiKey = this.dataset.bytescaleApiKey || window.BYTESCALE_API_KEY;

    this.handlers = {};

    if (this.form) {
      this.handlers.submit = () => this.handleSubmit();
      // Use a 'fake' submit button, because otherwise the captcha will execute .submit()
      // on the form, which bypasses the 'submit' event and can submit the form while stile uploading files.
      this.uploadButton.addEventListener('click', this.handlers.submit);
      window.Shopify.captcha.protect(this.form);

      // Inject file URLs into FormData right before submission
      this.form.addEventListener('formdata', (e) => {
        if (this.uploadedFiles) {
          const formatted = this.formatFileUrls(this.uploadedFiles);
          e.formData.set('contact[file_urls]', formatted);
        }
      });

      this.initFormValues();
    }
  }

  async handleSubmit() {
    if (this.isSubmitting) return;

    const files = this.fileUpload?.getFiles() || [];

    if (files.length > 0 && !this.uploadedFiles) {
      await this.processFileUpload(files);
    }
    else {
      this.form.requestSubmit();
      this.isSubmitting = false;
      this.setSubmitting(false);
    }
  }

  formatFileUrls(fileData) {
    if (!Array.isArray(fileData)) return '';

    return fileData
      .map((file, index) => {
        const url = typeof file === 'string' ? file : file.url || file.fileUrl || '';
        const name = file.name || `file-${index + 1}`;
        return url ? `${name} - ${url}` : '';
      })
      .filter(Boolean)
      .join('\n');
  }

  async processFileUpload(files) {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.setSubmitting(true);

    if (!this.validateBytescaleConfig()) {
      this.isSubmitting = false;
      this.setSubmitting(false);
      return;
    }

    try {
      notyf.success('Uploading files...');
      this.uploadedFiles = await this.uploadFiles(files);
      notyf.success('Files uploaded successfully!');
    }
    catch (error) {
      console.error('Upload error:', error);
      notyf.error('Failed to upload files. Please try again.');
      this.isSubmitting = false;
      this.setSubmitting(false);
      return;
    }

    this.isSubmitting = false;
    this.setSubmitting(false);

    if (this.fileUpload && typeof this.fileUpload.disableInput === 'function') {
      this.fileUpload.disableInput();
    }

    this.form.requestSubmit();
  }

  validateBytescaleConfig() {
    if (!this.bytescaleAccountId || !this.bytescaleApiKey) {
      notyf.error('Bytescale configuration is missing. Please contact support.');
      console.error('BYTESCALE_ACCOUNT_ID and BYTESCALE_API_KEY must be configured');
      return false;
    }
    return true;
  }

  async uploadFiles(files) {
    const uploadPromises = files.map(file => this.uploadFile(file));
    return await Promise.all(uploadPromises);
  }

  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const url = `https://api.bytescale.com/v2/accounts/${this.bytescaleAccountId}/uploads/form_data`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.bytescaleApiKey}`,
      },
      body: formData,
    });

    const result = await response.json();
    this.validateUploadResponse(response, result);

    const fileUrl = this.extractFileUrl(result, file.name);
    return { url: fileUrl,
      name: file.name };
  }

  validateUploadResponse(response, result) {
    if (Math.floor(response.status / 100) !== 2) {
      throw new Error(`Bytescale API Error: ${JSON.stringify(result)}`);
    }
  }

  extractFileUrl(result, fileName) {
    const data = (result.files && result.files[0]) || result;
    const fileUrl = data.fileUrl || data.url;

    if (!fileUrl || typeof fileUrl !== 'string') {
      throw new Error(`Failed to get valid URL for file: ${fileName}`);
    }
    return fileUrl;
  }

  setSubmitting(isSubmitting) {
    if (this.submitButton) {
      this.submitButton.disabled = isSubmitting;
      this.submitButton.textContent = isSubmitting ? 'Uploading...' : this.originalSubmitText;
    }
  }

  initFormValues() {
    const urlParams = new URLSearchParams(window.location.search);
    const prefillForm = urlParams.get('prefill_form');
    if (prefillForm !== 'true') return;
    urlParams.forEach((value, key) => {
      const input = this.form.querySelector(`#${key}`);
      if (!input) return;
      input.value = value;
    });
  }

  disconnectedCallback() {
    if (this.form && this.handlers.submit) {
      this.form.removeEventListener('submit', this.handlers.submit);
    }
  }
}

customElements.define('contact-form', ContactForm);
